import React, { useState } from "react";
import {
  Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
  Paper, Grid, TextField, MenuItem, CircularProgress, Divider,
  Snackbar, Alert, Card, CardContent
} from "@mui/material";
import {
  NavigateNext, ArrowBack, Person, Badge, AccountBalance,
  School, Work, Psychology, Save, Clear
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  accent: '#FFC107',
  accentDark: '#FFA000',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037',
  white: '#FFFFFF',
  filterBg: "#FFF9E6"
};

const AddStaffScreen = () => {
  const navigation = useNavigate();
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Initial Empty State
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    mobileNo: "",
    gender: "",
    dob: "",
    education: "",
    experience: "",
    skills: "",
    role: "Tutor", // Default
    status: "A",
    bankName: "",
    bankAccountNo: "",
    registerDate: new Date().toISOString().split('T')[0] // Default today
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const resJson = await response.json();

      if (resJson.status === 1) {
        setSnackbar({ open: true, message: "Staff registered successfully!", severity: "success" });
        setTimeout(() => navigation("/Portal/Staff"), 1500);
      } else {
        setSnackbar({ open: true, message: resJson.message || "Failed to create staff", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Connection Error", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const FormatMobileNo = (phoneNo) => {
    let cleaned = phoneNo.replace(/\D/g, '');
    if (cleaned.startsWith('60')) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  }

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

        {/* Navigation & Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
              <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
              <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Staff")} sx={{ cursor: 'pointer' }}>Staff</Link>
              <Typography color="text.primary">Register New Staff</Typography>
            </Breadcrumbs>
            <Header title="Staff Registration" subtitle="Add a new member to the Genius Milenia team" />
          </Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Staff")} color="inherit">Back to List</Button>
        </Stack>

        <form onSubmit={handleCreate}>
          <Grid container spacing={3}>

            {/* 1. PERSONAL DETAILS */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                  <Person sx={{ color: colors.textMain }} />
                  <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Full Name" name="fullname" value={formData.fullname} onChange={handleChange} size="small" required sx={{ bgcolor: 'white' }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Mobile Number" name="mobileNo" value={FormatMobileNo(formData.mobileNo)} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Gender" name="gender" value={formData.gender} onChange={handleChange} size="small" required sx={{ bgcolor: 'white' }}>
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Education / Qualifications" name="education" value={formData.education} onChange={handleChange} size="small" placeholder="e.g. Bachelor in Education, UM" sx={{ bgcolor: 'white' }} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 2. ROLE & STATUS */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3, height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                  <Badge sx={{ color: colors.accent }} />
                  <Typography variant="h6" fontWeight="bold">Role & Access</Typography>
                </Stack>
                <Stack spacing={2.5}>
                  <TextField select fullWidth label="Staff Role" name="role" value={formData.role} onChange={handleChange} size="small">
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Tutor">Tutor</MenuItem>
                    <MenuItem value="IT">IT Support</MenuItem>
                  </TextField>
                  <TextField select fullWidth label="Status" name="status" value={formData.status} onChange={handleChange} size="small">
                    <MenuItem value="A">Active</MenuItem>
                    <MenuItem value="I">Inactive</MenuItem>
                  </TextField>
                  <TextField fullWidth label="Joined Date" name="joinedDate" type="date" value={formData.registerDate} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} />
                </Stack>
              </Paper>
            </Grid>

            {/* 3. EXPERIENCE & SKILLS */}
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC' }}>
                <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Work fontSize="small" />
                  <Typography fontWeight="bold">Professional Experience</Typography>
                </Box>
                <CardContent>
                  <TextField fullWidth multiline rows={4} name="experience" value={formData.experience} onChange={handleChange} placeholder="Briefly describe previous work history..." />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC' }}>
                <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Psychology fontSize="small" />
                  <Typography fontWeight="bold">Skills & Competencies</Typography>
                </Box>
                <CardContent>
                  <TextField fullWidth multiline rows={4} name="skills" value={formData.skills} onChange={handleChange} placeholder="List skills separated by commas (e.g. Maths, Coaching, Management)" />
                </CardContent>
              </Card>
            </Grid>

            {/* 4. BANKING (Conditional for Tutors) */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${formData.role === 'Tutor' ? colors.accent : '#F0E6CC'}`, bgcolor: formData.role === 'Tutor' ? '#FFFEF5' : '#FFF' }}>
                <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance fontSize="small" />
                  <Typography fontWeight="bold">Banking Information {formData.role !== 'Tutor' && '(Optional)'}</Typography>
                </Box>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} size="small" placeholder="e.g. MAYBANK" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Account Number" name="bankAccountNo" value={formData.bankAccountNo} onChange={handleChange} size="small" />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SUBMIT BUTTONS */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigation("/Portal/Staff")} startIcon={<Clear />} sx={{ borderRadius: 2 }}>Discard</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  sx={{ bgcolor: colors.accent, color: 'black', px: 6, borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}
                >
                  {saving ? "Registering..." : "Register Staff"}
                </Button>
              </Stack>
            </Grid>

          </Grid>
        </form>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default AddStaffScreen;